<script setup lang="ts">
import { computed, h, ref, watch } from 'vue';
import { useDialog, type SelectOption } from 'naive-ui';
import type {
  ContractGenerationTemplateVariantSummary,
  ContractGenerationTemplateInDb,
  TemplateValidationError,
} from '@/api';
import { useOfficeTemplateStore } from '@/office/stores/officeTemplateStore';
import { TemplateVariantsService } from '@/services/contractGeneration/templateVariantsService';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaChoiceCard from '@/components/design/base/GaiaChoiceCard.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaRemoteSelect from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

type SaveMode = 'new-template' | 'new-variant' | 'new-version';

const props = defineProps<{ show: boolean }>();
const emit = defineEmits<{ (e: 'update:show', value: boolean): void }>();

const store = useOfficeTemplateStore();
const message = useGaiaToast();
const dialog = useDialog();

/**
 * Shown when the document fails validation just before upload. Lets the user
 * upload anyway (we never block) or cancel. Resolves true to proceed.
 */
function confirmInvalidUpload(errors: TemplateValidationError[]): Promise<boolean> {
  return new Promise(resolve => {
    let decided = false;
    const settle = (value: boolean): void => {
      if (decided) return;
      decided = true;
      resolve(value);
    };
    const shown = errors.slice(0, 6);
    const extra = errors.length - shown.length;
    dialog.warning({
      title: 'Validation found issues',
      content: () =>
        h('div', [
          h(
            'p',
            { style: 'margin: 0 0 8px' },
            `This document didn't pass template validation (${errors.length} issue${
              errors.length === 1 ? '' : 's'
            }). You can still upload it.`
          ),
          h(
            'ul',
            { style: 'margin: 0; padding-left: 18px' },
            shown.map(item => h('li', item.message))
          ),
          extra > 0 ? h('p', { style: 'margin: 8px 0 0' }, `…and ${extra} more.`) : null,
        ]),
      positiveText: 'Upload anyway',
      negativeText: 'Cancel',
      onPositiveClick: () => {
        settle(true);
      },
      onNegativeClick: () => {
        settle(false);
      },
      onClose: () => {
        settle(false);
      },
      onMaskClick: () => {
        settle(false);
      },
    });
  });
}

const showModal = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});

const SAVE_MODE_OPTIONS: readonly { label: string; meta: string; value: SaveMode }[] = [
  {
    label: 'New template',
    meta: 'Start a template from this document, with its first variant.',
    value: 'new-template',
  },
  {
    label: 'New variant of an existing template',
    meta: 'Add this document as another variant — a language, a country, a wording.',
    value: 'new-variant',
  },
  {
    label: 'New version of an existing variant',
    meta: 'Replace a variant’s file with this document; the template keeps its history.',
    value: 'new-version',
  },
];

const mode = ref<SaveMode>('new-template');

// New-template fields (same trio as the web TemplateCreateModal).
const name = ref<string>('');
const variantName = ref<string>('');
const description = ref<string>('');

// Target for new-variant / new-version modes. The full object is kept (not
// just the uid) so the select keeps showing its name even when a new
// search/page no longer contains it.
const targetTemplateUid = ref<string | null>(null);
const targetTemplate = ref<ContractGenerationTemplateInDb | null>(null);
const targetFiles = ref<ContractGenerationTemplateVariantSummary[]>([]);
const targetFilesLoading = ref<boolean>(false);
const targetFileUid = ref<string | null>(null);

const error = ref<string | null>(null);

function resetForm(): void {
  name.value = '';
  variantName.value = '';
  description.value = '';
  error.value = null;
  // Prefill from the library tab's working context: editing an open template
  // and saving it back as a new version is the most common loop.
  targetTemplateUid.value = store.selectedTemplateUid;
  targetTemplate.value = store.selectedTemplate;
  targetFileUid.value = store.selectedVariantUid;
  mode.value = store.selectedVariantUid ? 'new-version' : 'new-template';
}

watch(
  () => props.show,
  value => {
    if (value) {
      resetForm();
      // Fresh first page so the picker reflects templates created elsewhere
      // since the last search.
      void handleTemplateSearch('');
      if (targetTemplateUid.value) void loadTargetFiles(targetTemplateUid.value);
    }
  }
);

const templateOptions = computed<SelectOption[]>(() => {
  const options: SelectOption[] = store.templates.map(template => ({
    label: template.name,
    value: template.uid,
  }));
  const target = targetTemplate.value;
  if (target && !store.templates.some(template => template.uid === target.uid)) {
    options.unshift({ label: target.name, value: target.uid });
  }
  return options;
});

// The field does the scroll math and emits `load-more`.
function loadMoreTemplates(): void {
  void store.loadMoreTemplates();
}

async function handleTemplateSearch(query: string): Promise<void> {
  try {
    await store.searchTemplates(query);
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to search templates.'));
  }
}

async function loadTargetFiles(templateUid: string): Promise<void> {
  targetFilesLoading.value = true;
  try {
    const files = await TemplateVariantsService.list(templateUid);
    if (targetTemplateUid.value !== templateUid) return;
    targetFiles.value = files;
    if (targetFileUid.value && !files.some(file => file.uid === targetFileUid.value)) {
      targetFileUid.value = null;
    }
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to load template variants.'));
  } finally {
    if (targetTemplateUid.value === templateUid) {
      targetFilesLoading.value = false;
    }
  }
}

function handleTargetTemplateSelect(uid: string | null): void {
  targetTemplateUid.value = uid;
  targetTemplate.value = uid
    ? (store.templates.find(template => template.uid === uid) ?? targetTemplate.value)
    : null;
  targetFileUid.value = null;
  targetFiles.value = [];
  if (uid) void loadTargetFiles(uid);
}

const targetFileOptions = computed<SelectOption[]>(() =>
  targetFiles.value.map(file => ({
    label: `${file.name} — ${file.file_name}`,
    value: file.uid,
    disabled: !file.vault_file_uid,
  }))
);

/** Case-insensitive, as in the web modal: "German" and "german" would read as
 *  the same variant to anyone scanning the list. */
const variantNameAlreadyUsed = computed<boolean>(
  () =>
    mode.value === 'new-variant' &&
    variantName.value.trim().length > 0 &&
    targetFiles.value.some(
      file => file.name.toLowerCase() === variantName.value.trim().toLowerCase()
    )
);

const canSubmit = computed<boolean>(() => {
  if (store.savingDocument) return false;
  if (mode.value === 'new-template') {
    return name.value.trim().length > 0 && variantName.value.trim().length > 0;
  }
  if (!targetTemplateUid.value) return false;
  if (mode.value === 'new-variant') {
    return variantName.value.trim().length > 0 && !variantNameAlreadyUsed.value;
  }
  return targetFileUid.value !== null;
});

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  error.value = null;
  try {
    // Each save action validates the exported doc first; if it fails, it
    // calls confirmInvalidUpload and returns false when the user cancels.
    let saved = false;
    if (mode.value === 'new-template') {
      saved = await store.saveDocumentAsNewTemplate(
        name.value.trim(),
        variantName.value.trim(),
        description.value.trim() || null,
        confirmInvalidUpload
      );
      if (saved) message.success('Template created — validating…');
    } else if (mode.value === 'new-variant') {
      const templateUid = targetTemplateUid.value;
      if (!templateUid) return;
      saved = await store.saveDocumentAsNewVariant(
        templateUid,
        variantName.value.trim(),
        confirmInvalidUpload
      );
      if (saved) message.success('Variant added — validating…');
    } else {
      const templateUid = targetTemplateUid.value;
      const file = targetFiles.value.find(f => f.uid === targetFileUid.value);
      if (!templateUid || !file) return;
      saved = await store.saveDocumentAsNewVersion(templateUid, file, confirmInvalidUpload);
      if (saved) message.success(`Uploaded new version — re-validating “${file.name}”…`);
    }
    // Keep the modal open when the user cancelled at the validation prompt.
    if (saved) emit('update:show', false);
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to save the document as a template.');
  }
}
</script>

<template>
  <gaia-modal
    v-model:show="showModal"
    width="min(480px, calc(100vw - 24px))"
    :mask-closable="!store.savingDocument"
    :closable="!store.savingDocument"
  >
    <template #header>
      <gaia-header :level="2" size="dialog">Save document as template</gaia-header>
    </template>

    <n-flex vertical :size="16" :wrap="false">
      <gaia-form-field label="Save as">
        <n-flex vertical :size="6" :wrap="false">
          <gaia-choice-card
            v-for="option in SAVE_MODE_OPTIONS"
            :key="option.value"
            :label="option.label"
            :meta="option.meta"
            :selected="mode === option.value"
            :disabled="store.savingDocument"
            @select="mode = option.value"
          />
        </n-flex>
      </gaia-form-field>

      <template v-if="mode === 'new-template'">
        <gaia-form-field label="Template name" required>
          <gaia-input
            v-model:value="name"
            placeholder="Service Agreement"
            :disabled="store.savingDocument"
          />
        </gaia-form-field>
        <gaia-form-field label="Variant name" required hint="a language, a country, a wording">
          <gaia-input
            v-model:value="variantName"
            placeholder="e.g. German, Freelancer (Germany)"
            :disabled="store.savingDocument"
          />
        </gaia-form-field>
        <gaia-form-field label="Description" hint="Optional">
          <gaia-textarea
            v-model:value="description"
            :rows="2"
            placeholder="What this template is for"
            :disabled="store.savingDocument"
            :autosize="false"
          />
        </gaia-form-field>
      </template>

      <template v-else>
        <gaia-form-field label="Template" required>
          <gaia-remote-select
            :value="targetTemplateUid"
            :options="templateOptions"
            :loading="store.templatesLoading"
            placeholder="Search templates…"
            style="width: 100%"
            :disabled="store.savingDocument"
            @search="handleTemplateSearch"
            @load-more="loadMoreTemplates"
            @update:value="handleTargetTemplateSelect"
          />
        </gaia-form-field>

        <gaia-form-field
          v-if="mode === 'new-variant'"
          label="Variant name"
          required
          hint="a language, a country, a wording"
        >
          <gaia-input
            v-model:value="variantName"
            placeholder="e.g. German, Freelancer (Germany)"
            :disabled="store.savingDocument"
          />
        </gaia-form-field>
        <gaia-banner v-if="variantNameAlreadyUsed" plain type="warning">
          This template already has a “{{ variantName.trim() }}” variant. Pick “New version” instead
          to update it.
        </gaia-banner>

        <gaia-form-field v-if="mode === 'new-version'" label="Variant to update" required>
          <gaia-select
            v-model:value="targetFileUid"
            :options="targetFileOptions"
            :loading="targetFilesLoading"
            placeholder="Pick a variant…"
            style="width: 100%"
            :disabled="store.savingDocument || !targetTemplateUid"
          />
        </gaia-form-field>
      </template>

      <gaia-text size="meta" tone="subtle" :style="{ lineHeight: 1.5 }">
        The current content of this Word document — including unsaved edits — is uploaded and
        validated.
      </gaia-text>

      <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button :disabled="store.savingDocument" @click="showModal = false"
          >Cancel</gaia-button
        >
        <gaia-button
          type="primary"
          :loading="store.savingDocument"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ mode === 'new-version' ? 'Upload new version' : 'Create' }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
